import React from 'react';
import { Button } from './Button';

interface AISuggestedSolutionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketTitle: string;
  isLoading: boolean;
  errorText: string | null;
  solutionsText: string | null;
}

const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center space-x-2 my-4">
    <div className="w-3 h-3 bg-slate-500 rounded-full animate-pulse delay-0"></div>
    <div className="w-3 h-3 bg-slate-500 rounded-full animate-pulse delay-150"></div>
    <div className="w-3 h-3 bg-slate-500 rounded-full animate-pulse delay-300"></div>
    <span className="text-slate-600 text-sm">Searching knowledge base...</span>
  </div>
);

const ErrorIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-red-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

const renderFormattedText = (text: string | null): React.ReactNode => {
    if (!text) return null;
    
    // Basic Markdown-like replacements
    // Ensure this is safe if text can come from untrusted sources; for AI, it's generally okay.
    let html = text;

    // Bold: **text** or __text__
    html = html.replace(/\*\*(.*?)\*\*|__(.*?)__/g, '<strong>$1$2</strong>');
    // Italic: *text* or _text_
    html = html.replace(/\*(.*?)\*|_(.*?)_/g, '<em>$1$2</em>');
    // Strikethrough: ~~text~~
    html = html.replace(/~~(.*?)~~/g, '<del>$1</del>');
    // Inline Code: `code`
    html = html.replace(/`(.*?)`/g, '<code class="px-1 py-0.5 bg-slate-200 text-slate-700 rounded text-xs">$1</code>');
    
    // Lists (simplified: assumes lines starting with *,-, or number. are list items)
    // This is a very basic list handler. Complex nested lists won't work well.
    html = html.replace(/^(\s*([*+-]|\d+\.)\s+)(.*)/gm, (match, p1, p2, p3) => {
        const listType = (p2.match(/\d+\./)) ? 'ol' : 'ul';
        const prevLineBreak = html.lastIndexOf('\n', html.indexOf(match) -1) === -1 || html.substring(html.lastIndexOf('\n', html.indexOf(match) -1),html.indexOf(match) ).match(/^(\s*([*+-]|\d+\.)\s+)/m);
        const nextLineBreak = html.indexOf('\n', html.indexOf(match) + match.length ) === -1 || html.substring(html.indexOf(match) + match.length, html.indexOf('\n', html.indexOf(match) + match.length )).match(/^(\s*([*+-]|\d+\.)\s+)/m);

        let itemHtml = `<li>${p3.trim()}</li>`;
        if(!prevLineBreak) itemHtml = `<${listType} class="${listType === 'ol' ? 'list-decimal' : 'list-disc'} list-inside pl-5 my-2">${itemHtml}`;
        if(!nextLineBreak) itemHtml = `${itemHtml}</${listType}>`;
        return itemHtml;
    });
    
    // Paragraphs (treat double newlines as paragraph breaks)
    html = html.split(/\n\s*\n/).map(paragraph => `<p class="mb-2">${paragraph}</p>`).join('');
    // Single newlines to <br> (within paragraphs created above, or if no double newlines)
    html = html.replace(/\n/g, '<br />');

    return <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
};


export const AISuggestedSolutionsModal: React.FC<AISuggestedSolutionsModalProps> = ({
  isOpen,
  onClose,
  ticketTitle,
  isLoading,
  errorText,
  solutionsText,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[150]" // High z-index
      aria-labelledby="ai-solutions-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h2 id="ai-solutions-modal-title" className="text-lg font-semibold text-slate-800">
            AI Knowledge Base Results for: <span className="text-[var(--color-primary)]">{ticketTitle}</span>
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-grow overflow-y-auto pr-2">
          {isLoading && <LoadingSpinner />}
          {errorText && (
            <div className="flex items-center text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
              <ErrorIcon />
              <p>{errorText}</p>
            </div>
          )}
          {!isLoading && !errorText && solutionsText && (
            <div>{renderFormattedText(solutionsText)}</div>
          )}
          {!isLoading && !errorText && !solutionsText && (
            <p className="text-slate-500 text-sm">No solutions found or search not performed yet.</p>
          )}
        </div>

        <div className="border-t pt-4 mt-4 flex justify-end">
          <Button type="button" variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};
